import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchPartnerRequestById, inactivatePartnerRequest } from 'clickNgo/entities/partner/api'
import { PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useGetFormDataField } from 'cng-common/features/form'

import { PartnerEditorFormKeys, PartnerEditorFormSettings } from '../../../configs'
import { IPartnerEditorData } from '../../../models'
import { checkPossibilityChangeStatus } from '../helpers/checkPossibilityChangeStatus'

export const useInactivatePartner = () => {
  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )

  const [isInactive] = useGetFormDataField<boolean>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.IsInactive
  )

  const [isPartnerInactivationPossible, setIsPartnerInactivationPossible] = useState(false)
  const [partnerInactivationImpossibleMessage, setPartnerInactivationImpossibleMessage] =
    useState<string>()
  const [partnerInactivationErrorMessages, setPartnerInactivationErrorMessages] = useState<
    string[]
  >([])

  const latestActualData = useRef(actualData)
  latestActualData.current = actualData

  const inactivatePartner = useCallback(async () => {
    setPartnerInactivationErrorMessages([])

    const { partnerId, locationId } = latestActualData.current.partner
    const result = await inactivatePartnerRequest({ partnerId, locationId })
    const errorMessages = getJSendErrorMessages(result)

    if (errorMessages.length) {
      setPartnerInactivationErrorMessages(errorMessages)

      return
    }

    const partner = await fetchPartnerRequestById(partnerId, { isForce: true })
    const current = latestActualData.current

    updateActualData({
      ...current,
      partner: partner ?? {
        ...current.partner,
        status: result.data.partnerStatus,
        workflowStatus: PartnerWorkflowStatus.NEW,
      },
    })
  }, [updateActualData])

  useEffect(() => {
    let isOutdated = false

    if (actualData.partner) {
      checkPossibilityChangeStatus(actualData.partner)
        .catch(() => 'Could not check whether this partner can be inactivated')
        .then((result) => {
          if (isOutdated) {
            return
          }

          setPartnerInactivationImpossibleMessage(result || undefined)
          setIsPartnerInactivationPossible(!result)
        })
    }

    return () => {
      isOutdated = true
    }
  }, [actualData.partner])

  return {
    isInactive,
    inactivatePartner,
    partnerInactivationImpossibleMessage,
    partnerInactivationErrorMessages,
    isPartnerInactivationPossible,
  }
}
